import React, { useState, useCallback, useEffect } from 'react';
import { getProductAndForms } from "../../../../api/nebProductService";
import { ClaySelect, ClayInput } from '@clayui/form';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportToPdf, exportToExcel } from './exportToPdf';
import { NumericFormat } from 'react-number-format';
import { Row, Col, Card, Form, InputGroup, Modal, Button, Table, Badge, Accordion } from 'react-bootstrap';

const C1TableCheckingKtoeDP = ({
  agencyId,
  checkingReportOriginalUnit,
  checkingReportKtoe,
  totalSellouts,
  productAndFormsList,
}) => {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [reportBasicInfo, setReportBasicInfo] = useState(null);
  const [c1KtoeLoading, setC1KtoeLoading] = useState(true);

  const numInputStyle = {
    textAlign: "right",
    width: "100%",
    height: "40px",
    marginRight: "5px",
    borderRadius: "5px",
    background: "#f8f8f8",
    color: "black",
  };

  const selectInputStyle = {
    width: "100%",
    height: "40px",
    marginRight: "5px",
    borderRadius: "5px",
    background: "#f8f8f8",
    color: "black",
  };

  // Initialization function
  const initialize = () => {
    // Generate headers
    const dynamicHeaders = [
      { id: "no", name: "No." },
      { id: "product", name: "Product" },
      { id: "unit", name: "Unit" },
      { id: "balance", name: "Balance" },
      ...(productAndFormsList.length > 0
        ? productAndFormsList[0].forms.map((form) => ({
          id: form.formFieldCode,
          name: form.formFieldName,
        }))
        : []),
      { id: "totalSellout", name: "Total Sell Out`" }
    ];
    setHeaders(dynamicHeaders);

    const dynamicRows = productAndFormsList.map((product, index) => {
      const isStandalone = product.standalone;

      // Assign values to product forms using checkingReportKtoe
      product.forms.forEach((form) => {
        checkingReportKtoe.forms.forEach((formCategory) => {
          formCategory.categories.forEach((category) => {
            category.products.forEach((fetchedProduct) => {
              if (
                fetchedProduct.fieldName === product.productName &&
                category.formCode === form.formFieldCode
              ) {
                // Assign the fetched value to the form
                form.value = Number(fetchedProduct.value || 0).toFixed(4);

                // Loop through checkingReportOriginalUnit to find the original unit
                checkingReportOriginalUnit.forms.forEach((originalFormCategory) => {
                  originalFormCategory.categories.forEach((originalCategory) => {
                    originalCategory.products.forEach((originalProduct) => {
                      if (originalProduct.fieldName === fetchedProduct.fieldName) {
                        form.originalunit = originalProduct.unit || ""; // Assign original unit
                      }
                    });
                  });
                });

                // Retrieve the inKtoe value from the product.units array
                const matchedUnit = product.units.find(
                  (unit) => unit.productUnitName === form.originalunit
                );
                if (matchedUnit) {
                  form.inKtoe = matchedUnit.inKtoe || 0; // Assign the inKtoe value
                } else {
                  form.inKtoe = 0; // Fallback in case no match is found
                }

                form.productName = product.productName;
              }
            });
          });
        });
      });

      // Calculate ktoeConversionFactor based on productName
      const ktoeConversionFactor = (
        product.forms.find((form) => form.productName === product.productName)?.inKtoe || 1
      );

      // Calculate balance
      const newBalance = product.forms.reduce((formSum, form) => {
        const formBalance = Number(form.value || 0).toFixed(4); // Use the assigned value
        return formSum + parseFloat(formBalance);
      }, 0);

      const totalSellout = totalSellouts.find((sellout) => sellout[product.productName] !== undefined);
      const totalSelloutBalance = totalSellout ? totalSellout[product.productName] : 0;

      // Convert totalSellout to ktoe
      const totalSelloutInKtoe = (totalSelloutBalance * ktoeConversionFactor).toFixed(4);

      // Final calculated balance
      const finalBalance = (parseFloat(totalSelloutInKtoe) - newBalance).toFixed(2);

      return {
        no: index + 1,
        product: product.productName,
        unit: (
          <ClaySelect
            disabled
            style={selectInputStyle}
            defaultValue="ktoe" // Default unit for now; adjust as necessary
          >
            <option value="ktoe">ktoe</option>
            {product.units.map((unit, unitIndex) => (
              <option key={unitIndex} value={unit.productUnitName}>
                {unit.productUnitName}
              </option>
            ))}
          </ClaySelect>
        ),
        ...product.forms.reduce((acc, form) => {
          acc[form.formFieldCode] = (
            <NumericFormat
              disabled
              key={`${index}_${form.formFieldCode}`}
              style={numInputStyle}
              value={form.value || 0} // Use the assigned value for NumericFormat
              thousandSeparator
              customInput={ClayInput}
            />
          );
          return acc;
        }, {}),
        totalSellout: (
          <span
            style={{
              paddingRight: "10px",
              display: "block",
              textAlign: "right",
            }}
          >
            <NumericFormat
              displayType="text"
              value={totalSelloutInKtoe} // Total Sell Out in KTOE
              thousandSeparator
            />
          </span>
        ),
        balance: (
          <span
            style={{
              paddingRight: "10px",
              display: "block",
              textAlign: "right",
            }}
          >
            <NumericFormat
              displayType="text"
              value={finalBalance} // Use the rounded final balance
              thousandSeparator
            />
          </span>
        ),
      };
    });

    setRows(dynamicRows);

    // Initialize report info if available
    if (checkingReportKtoe) {
      setReportBasicInfo({
        year: checkingReportKtoe.reportPeriodYear,
        quarter: checkingReportKtoe.reportQuarter,
        agency: checkingReportKtoe.agency.agencyName,
        dataProvider: checkingReportKtoe.dataProvider.name,
        status: checkingReportKtoe.reportStatus,
        type: "Final Energy Consumption Sectors (C1) (KTOE)",
      });
    }

    setC1KtoeLoading(false);
  };

  // Initialize on component mount
  useEffect(() => {
    initialize();
  }, [productAndFormsList, checkingReportKtoe, totalSellouts]);

  return (
    <div>
      {/* Export to PDF */}
      <FontAwesomeIcon
        title="Download as PDF"
        style={{ cursor: 'pointer' }}
        onClick={() => exportToPdf("c1-ktoe-table", reportBasicInfo)}
        className='mr-2 text-primary'
        size='xl'
        icon={faFilePdf}
      />

      {/* Export to Excel */}
      <FontAwesomeIcon
        title="Download as Excel"
        style={{ cursor: 'pointer' }}
        onClick={() => exportToExcel("c1-ktoe-table", reportBasicInfo)}
        className='text-primary'
        size='xl'
        icon={faFileExcel}
      />

      {c1KtoeLoading ? <div>Loading...</div> :
        <Table responsive bordered hover size="sm" className="mt-3" id="c1-ktoe-table">
          <thead>
            <tr>
              {headers.map((column) => (
                <th
                  key={column.id}
                  className={
                    column.id === 'no' || column.id === 'balance' || column.id === 'totalSellout' ?
                      "table-cell-no-width"
                      : "table-cell-minw-200"}
                  style={
                    column.id === 'product'
                      ? {
                        position: 'sticky',
                        left: 0,  // First sticky column
                        backgroundColor: '#ECECEF',
                        zIndex: 1,  // Ensure it's above other cells
                      }
                      : column.id === 'balance'
                        ? {
                          position: 'sticky',
                          left: '199px',  // Adjust based on column width
                          backgroundColor: '#ECECEF',
                          zIndex: 1,  // Ensure it's above other cells
                        }
                        : {}
                  }
                >
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map(row => (
              <tr key={row.no}>
                {headers.map(column => (
                  <td
                    key={column.id}
                    className={
                      column.id === 'no' || column.id === 'balance' || column.id === 'totalSellout' ?
                        "table-cell-no-width"
                        : "table-cell-minw-200"}
                    style={
                      column.id === 'product'
                        ? {
                          position: 'sticky',
                          left: 0,  // First sticky column
                          backgroundColor: '#f7f7f7',
                          zIndex: 1,  // Ensure it's above other cells
                        }
                        : column.id === 'balance'
                          ? {
                            position: 'sticky',
                            left: '199px',  // Adjust based on column width
                            backgroundColor: '#f7f7f7',
                            zIndex: 1,  // Ensure it's above other cells
                          }
                          : {}
                    }
                  >
                    {row[column.id]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      }

    </div>

  );
};

export default C1TableCheckingKtoeDP;
